import { useContext, useState, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { saveGatepassApi, getGatepassDetailApi, updateGatepassApi, getCustomersApi } from '../api/api.js';
import { AuthContext } from '../context/AuthContext.jsx';
import { useCompany } from '../context/CompanyContext.jsx';
import Card from '../components/ui/Card.jsx';
import Button from '../components/ui/Button.jsx';
import FormField from '../components/ui/FormField.jsx';
import 'react-datepicker/dist/react-datepicker.css';

const SHED_OPTIONS = [
  { value: 'Shed1', label: 'Shed1' },
  { value: 'Shed2', label: 'Shed2' },
  { value: 'Shed3', label: 'Shed3' },
];

const PRODUCT_OPTIONS = [
  { value: 'Potato Sona Chips', label: 'Potato Sona Chips' },
  { value: 'Sugar Free Potato', label: 'Sugar Free Potato' },
  { value: 'New Patato', label: 'New Patato' },
];

function validateForm(form, gatepassDate, selectedShed, selectedProduct, selectedKisan) {
  const errors = {};
  if (!(form.gatepassNo || '').trim()) errors.gatepassNo = 'Gatepass No is required';
  if (!gatepassDate) errors.gatepassDate = 'Gatepass Date is required';
  const kisanVal = selectedKisan?.value ?? (form.kisanName || '').trim();
  if (!kisanVal) errors.kisanName = 'Kisan Name is required';
  if (!selectedShed?.value) errors.shed = 'Shed is required';
  if (!selectedProduct?.value) errors.productName = 'Product Name is required';
  if (!(form.bag || '').trim()) errors.bag = 'Bag is required';
  if (form.qtl === '' || form.qtl == null) errors.qtl = 'Qtl is required';
  else if (isNaN(Number(form.qtl))) errors.qtl = 'Qtl must be a number';
  if (!(form.vehicleNo || '').trim()) errors.vehicleNo = 'Vehicle No is required';
  return errors;
}

export default function GatepassEntryForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useContext(AuthContext);
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const [gatepassDate, setGatepassDate] = useState(new Date());
  const [selectedShed, setSelectedShed] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedKisan, setSelectedKisan] = useState(null);
  const [kisanOptions, setKisanOptions] = useState([]);
  const [form, setForm] = useState({
    gatepassNo: '',
    kisanName: '',
    bag: '',
    qtl: '',
    vehicleNo: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(isEdit);
  const [msg, setMsg] = useState('');

  // Load customer list (same as VoucherController GetCustomerList) for Kisan Name dropdown
  useEffect(() => {
    getCustomersApi({ limit: 500 })
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(data) ? data : [];
        const options = list
          .map((row) => (row.customerName ? { value: row.customerName, label: row.customerName } : null))
          .filter(Boolean);
        setKisanOptions(options);
      })
      .catch(() => setKisanOptions([]));
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingDetail(true);
    getGatepassDetailApi(id)
      .then((res) => {
        const d = res.data?.data ?? res.data;
        if (d) {
          setForm({
            gatepassNo: d.gatepassNo ?? '',
            kisanName: d.kisanName ?? '',
            bag: d.bag ?? '',
            qtl: d.qtl != null ? String(d.qtl) : '',
            vehicleNo: d.vehicleNo ?? '',
          });
          setGatepassDate(d.gatepassDate ? new Date(d.gatepassDate) : new Date());
          setSelectedShed(d.shed ? SHED_OPTIONS.find((o) => o.value === d.shed) ?? { value: d.shed, label: d.shed } : null);
          setSelectedProduct(d.productName ? PRODUCT_OPTIONS.find((o) => o.value === d.productName) ?? { value: d.productName, label: d.productName } : null);
          setSelectedKisan(d.kisanName ? { value: d.kisanName, label: d.kisanName } : null);
        }
      })
      .catch(() => setMsg('Failed to load gatepass.'))
      .finally(() => setLoadingDetail(false));
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleDateChange = (date) => {
    if (date) setGatepassDate(date);
    if (errors.gatepassDate) setErrors((prev) => ({ ...prev, gatepassDate: '' }));
  };

  const handleShedChange = (opt) => {
    setSelectedShed(opt);
    if (errors.shed) setErrors((prev) => ({ ...prev, shed: '' }));
  };

  const handleProductChange = (opt) => {
    setSelectedProduct(opt);
    if (errors.productName) setErrors((prev) => ({ ...prev, productName: '' }));
  };

  const handleKisanChange = (opt) => {
    setSelectedKisan(opt);
    setForm((prev) => ({ ...prev, kisanName: opt?.value ?? '' }));
    if (errors.kisanName) setErrors((prev) => ({ ...prev, kisanName: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm(form, gatepassDate, selectedShed, selectedProduct, selectedKisan);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setSaving(true);
    setMsg('');
    try {
      const kisanName = (selectedKisan?.value ?? (form.kisanName || '').trim()).trim();
      const payload = {
        gatepassNo: (form.gatepassNo || '').trim(),
        gatepassDate: gatepassDate.toISOString().slice(0, 10),
        kisanName,
        shed: selectedShed.value,
        productName: selectedProduct.value,
        bag: (form.bag || '').trim(),
        qtl: Number(form.qtl),
        vehicleNo: (form.vehicleNo || '').trim(),
      };
      if (isEdit) {
        await updateGatepassApi(id, payload);
        setMsg('Gatepass updated successfully.');
      } else {
        await saveGatepassApi({
          ...payload,
          tallyId: user?.tallyId,
          companyId: companyId || null,
        });
        setMsg('Gatepass saved successfully.');
        setForm({ gatepassNo: '', kisanName: '', bag: '', qtl: '', vehicleNo: '' });
        setSelectedShed(null);
        setSelectedProduct(null);
        setSelectedKisan(null);
        setGatepassDate(new Date());
      }
      setTimeout(() => navigate('/gatepass/list'), 1500);
    } catch (err) {
      console.error(err);
      setMsg(isEdit ? 'Failed to update Gatepass.' : 'Failed to save Gatepass.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingDetail) {
    return (
      <Card title={isEdit ? 'Edit Gatepass' : 'Gatepass Entry'} className="gatepass-form-card">
        <p className="text-muted">Loading...</p>
      </Card>
    );
  }

  return (
    <Card title={isEdit ? 'Edit Gatepass' : 'Gatepass Entry'} className="gatepass-form-card">
      <form onSubmit={handleSubmit}>
        {msg && (
          <div className={`alert ${msg.includes('success') ? 'alert-success' : 'alert-danger'}`} role="alert">
            {msg}
          </div>
        )}
        <div className="row g-3">
          <div className="col-md-6">
            <FormField label="Gatepass No" id="gatepassNo" required error={errors.gatepassNo}>
              <input
                type="text"
                id="gatepassNo"
                name="gatepassNo"
                className={`form-control ${errors.gatepassNo ? 'is-invalid' : ''}`}
                value={form.gatepassNo}
                onChange={handleChange}
                placeholder="Gatepass number"
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Gatepass Date" id="gatepassDate" required error={errors.gatepassDate}>
              <ReactDatePicker
                selected={gatepassDate}
                onChange={handleDateChange}
                className={`form-control ${errors.gatepassDate ? 'is-invalid' : ''}`}
                dateFormat="dd-MM-yyyy"
                placeholderText="Select date"
                maxDate={new Date()}
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Kisan Name" id="kisanName" required error={errors.kisanName}>
              <CreatableSelect
                options={kisanOptions}
                value={selectedKisan}
                onChange={handleKisanChange}
                placeholder="Select or type Kisan name"
                isClearable
                isSearchable
                formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                className={`react-select-container ${errors.kisanName ? 'is-invalid' : ''}`}
                classNamePrefix="react-select"
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Shed" id="shed" required error={errors.shed}>
              <Select
                options={SHED_OPTIONS}
                value={selectedShed}
                onChange={handleShedChange}
                placeholder="Select Shed"
                isClearable
                isSearchable={false}
                className={`react-select-container ${errors.shed ? 'is-invalid' : ''}`}
                classNamePrefix="react-select"
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Product Name" id="productName" required error={errors.productName}>
              <Select
                options={PRODUCT_OPTIONS}
                value={selectedProduct}
                onChange={handleProductChange}
                placeholder="Select Product"
                isClearable
                isSearchable={false}
                className={`react-select-container ${errors.productName ? 'is-invalid' : ''}`}
                classNamePrefix="react-select"
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Bag" id="bag" required error={errors.bag}>
              <input
                type="text"
                id="bag"
                name="bag"
                className={`form-control ${errors.bag ? 'is-invalid' : ''}`}
                value={form.bag}
                onChange={handleChange}
                placeholder="Bag"
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Qtl" id="qtl" required error={errors.qtl}>
              <input
                type="number"
                id="qtl"
                name="qtl"
                className={`form-control ${errors.qtl ? 'is-invalid' : ''}`}
                value={form.qtl}
                onChange={handleChange}
                placeholder="Qtl"
                step="any"
                min="0"
              />
            </FormField>
          </div>
          <div className="col-md-6">
            <FormField label="Vehicle No" id="vehicleNo" required error={errors.vehicleNo}>
              <input
                type="text"
                id="vehicleNo"
                name="vehicleNo"
                className={`form-control ${errors.vehicleNo ? 'is-invalid' : ''}`}
                value={form.vehicleNo}
                onChange={handleChange}
                placeholder="Vehicle number"
              />
            </FormField>
          </div>
        </div>
        <div className="mt-3 d-flex gap-2">
          <Button type="submit" disabled={saving}>
            {saving ? (isEdit ? 'Updating...' : 'Saving...') : (isEdit ? 'Update Gatepass' : 'Save Gatepass')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/gatepass/list')}>
            View List
          </Button>
        </div>
      </form>
    </Card>
  );
}
